import { useState } from "react";
import { PUBLIC_API as API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

function CopyBtn({ text, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <Button data-testid={testid} variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
      onClick={() => { navigator.clipboard.writeText(text); setOk(true); toast.success("Copiado"); setTimeout(() => setOk(false), 1500); }}>
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

export function IntegrationManual({ sampleKey }) {
  const key = sampleKey || "itk_SUA_CHAVE_AQUI";
  const curl = `curl -X POST ${API}/v1/tickets \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Erro no login","description":"Usuário não consegue autenticar","category":"software_desktop","priority":"alta","client_email":"operador@empresa.com.br"}'`;

  return (
    <div className="space-y-5" data-testid="integration-manual">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">URL Base</p>
        <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
          <code className="font-mono text-xs text-zinc-200 flex-1 truncate">{API}/v1</code>
          <CopyBtn text={`${API}/v1`} testid="copy-base-url" />
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Autenticação</p>
        <p className="text-sm text-zinc-400">Todas as chamadas exigem o header <code className="font-mono text-xs text-indigo-300">Authorization: Bearer &lt;API_KEY&gt;</code>. A chave é exibida apenas uma vez na criação — guarde-a com segurança.</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Endpoints</p>
        <div className="border border-zinc-800 rounded-md divide-y divide-zinc-800 text-sm">
          <div className="px-3 py-2 flex items-center gap-3"><span className="font-mono text-[11px] text-emerald-400 font-medium w-12">POST</span><code className="font-mono text-xs text-zinc-200">/v1/tickets</code><span className="text-zinc-500 text-xs ml-auto">Abrir chamado</span></div>
          <div className="px-3 py-2 flex items-center gap-3"><span className="font-mono text-[11px] text-sky-400 font-medium w-12">GET</span><code className="font-mono text-xs text-zinc-200">/v1/tickets</code><span className="text-zinc-500 text-xs ml-auto">Listar chamados (filtro ?status=)</span></div>
          <div className="px-3 py-2 flex items-center gap-3"><span className="font-mono text-[11px] text-sky-400 font-medium w-12">GET</span><code className="font-mono text-xs text-zinc-200">/v1/tickets/{"{id}"}</code><span className="text-zinc-500 text-xs ml-auto">Consultar chamado</span></div>
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Campos do chamado (POST /v1/tickets)</p>
        <div className="border border-zinc-800 rounded-md px-3 py-2 text-xs text-zinc-400 space-y-1">
          <p><code className="font-mono text-zinc-200">title</code> e <code className="font-mono text-zinc-200">description</code> — obrigatórios</p>
          <p><code className="font-mono text-zinc-200">category</code> — hardware | software_mobile | software_desktop | outros</p>
          <p><code className="font-mono text-zinc-200">priority</code> — baixa | media | alta | critica</p>
          <p><code className="font-mono text-zinc-200">client_email</code>, <code className="font-mono text-zinc-200">client_name</code>, <code className="font-mono text-zinc-200">external_ref</code> — opcionais (vincula o chamado ao cliente pelo e-mail, se existir)</p>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">Exemplo (cURL)</p>
          <CopyBtn text={curl} testid="copy-curl-example" />
        </div>
        <pre className="bg-zinc-950 border border-zinc-800 rounded-md p-3 overflow-x-auto font-mono text-[11px] leading-relaxed text-zinc-300">{curl}</pre>
      </div>
      <div className="bg-indigo-600/10 border border-indigo-500/20 rounded-md p-4">
        <p className="text-sm font-medium text-indigo-300 mb-1">Integração com o Brave Access</p>
        <p className="text-xs text-zinc-400 leading-relaxed">
          No painel do Brave Access (rota <code className="font-mono text-zinc-300">/cliente/dashboard</code>), adicione um botão "Suporte" que chame <code className="font-mono text-zinc-300">POST /v1/tickets</code> com os dados do operador logado em <code className="font-mono text-zinc-300">client_email</code>. O chamado aparecerá instantaneamente no painel do tenant e no portal do cliente.
        </p>
      </div>
    </div>
  );
}
